// Progress.jsx

import React from 'react';

// Context
import { useProgress } from 'context/ProgressContext';
import { useTheme } from 'context/ThemeContext';
import { useUI } from 'context/UIContext';
import { useUser } from 'context/UserContext';

// CSS
import './Progress.css';

// Components
import ReactDOM from 'react-dom'; 
import { CircleLoader } from 'react-spinners';

/**
 * Progress Component
 * 
 * This component renders a masked progress indicator with conditional styles.
 * 
 * @returns {ReactNode} The rendered component.
 */
const Progress = () => {

  // ----- CONTEXT ----- //

  // Global Context

  const { background } = useUI();
  const {
    progressMessage,
    progressDetails,
    progressVisible,
  } = useProgress();

  // User Context
  const { user } = useUser();

  // Theme Context
  const { theme } = useTheme();

  if (!progressVisible) return null;

  const overlayStyle = {
    backgroundColor: theme.overlay,
    backgroundImage: user
      ? `url(${user.wallpaperDark || 'none'})`
      : `url(${background})`, // Fallback if user is null
    backgroundSize: 'cover', // Ensures the image covers the entire screen
    backgroundRepeat: 'no-repeat', // Prevents the image from repeating
    backgroundAttachment: 'fixed', // Fixes the image to the screen, not the scrolling content
    height: '100vh', // Ensures it takes up the full viewport height
    width: '100vw', // Ensures it takes up the full viewport width
    left: 0,
    opacity: 1,
    position: 'fixed',
    top: 0,
    zIndex: 2000, // Behind the content
  };

  const content = (
    <>
      <div
        className="progress-mask"
        style={{
          ...overlayStyle, // Gradient image background
        }}
      ></div>

      {/* CONTENT */}
      <div
        className="progress-content"
        style={{
          left: '50%',
          position: 'fixed',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 2001,
        }}
      >

        {/* CIRCLE */}
        <div className="progress-circle">
          <CircleLoader
            size={40}
            color={theme.foregroundMuted}
            loading={true}
          />
        </div>

        {/* STATUS MESSAGE */}
        <div
          className="progress-status-message"
          style={{
            marginBottom: '1rem',
            textAlign: 'center',
          }}
        >
          {progressMessage}
        </div>

        {/* STATUS DETAILS */}
        <div
          className="progress-status-details"
          style={{
            marginBottom: '1rem',
            textAlign: 'center',
          }}
        >
          {progressDetails}
        </div>

      </div>
    </>
  );

    // ----- RENDER ----- //

    // Use createPage to render modalContent outside of the root hierarchy

    return ReactDOM.createPortal(content, document.getElementById('portal-root'));
};

// Dev Tools
Progress.displayName = 'Progress';

export default Progress;
